import './style/productRow.scss';
import {AwesomeIcon, nullify} from '@dvrd/dvr-controls';
import {IconName} from '@fortawesome/fontawesome-svg-core';
import classNames from 'classnames';
import React, {useRef} from 'react';
import {useTranslation} from 'react-i18next';
import BolProduct from '../../../models/bolProductModel';
import {ProductStatus} from '../../../util/productsUtil';
import {formatCurrency, onCopy} from '../../../util/utils';
import {translateVatValue} from '../../../util/vatUtil';
import {BluemateBadge, BluemateButton, BluemateImageFallback, BluemateMenu, BluemateTooltip} from '../../controls';
import {BluemateMenuOption} from '../../controls/menu/bluemateMenu';
import SpinnerIcon from '../../icon/spinnerIcon';
import CopyIcon from '../../misc/copyIcon';
import CountryFlag from '../../misc/countryFlag';
import {useInView} from '../../misc/useInView';

interface Props {
    product: BolProduct;
    index: number;
    status: ProductStatus;
    costs: number | null | undefined;
    downloading: boolean;
    updating: boolean;
    canEdit: boolean;
    onEditName: (product: BolProduct) => void;
    onEditVat: (product: BolProduct) => void;
    onEditCosts: (product: BolProduct) => void;
    onToggleHidden: (product: BolProduct) => void;
    onClickDownload: (product: BolProduct) => void;
    onVisible: (index: number, product: BolProduct) => void;
}

function ProductRow(props: Props) {
    const {
        product,
        index,
        status,
        costs,
        downloading,
        updating,
        canEdit,
        onEditName,
        onEditVat,
        onEditCosts,
        onToggleHidden,
        onClickDownload,
        onVisible
    } = props;
    const {t} = useTranslation();
    const rowRef = useRef<HTMLDivElement>(null);
    const canDownload = !!product.globalId;
    const isVisible = status === 'visible';
    const vatCountryCodes = product.vats.countryCodes;
    useInView(rowRef, () => onVisible(index, product), {resetKey: product});

    function onClickEditName() {
        onEditName(product);
    }

    function onClickEditVat() {
        onEditVat(product);
    }

    function onClickEditCosts() {
        onEditCosts(product);
    }

    function onClickToggleHidden() {
        onToggleHidden(product);
    }

    function onClickDownloadCode() {
        onClickDownload(product);
    }

    function onClickTitle(evt: React.MouseEvent) {
        if (!canEdit) return;
        evt.stopPropagation();
        onEditName(product);
    }

    function onClickVat(evt: React.MouseEvent) {
        if (!canEdit) return;
        evt.stopPropagation();
        onEditVat(product);
    }

    function onClickCosts(evt: React.MouseEvent) {
        evt.stopPropagation();
        onEditCosts(product);
    }

    function buildMenuOptions(): BluemateMenuOption[] {
        const options: BluemateMenuOption[] = [];
        if (canEdit)
            options.push(
                {
                    label: t('products.menu.editName', 'Naam aanpassen…'),
                    icon: <AwesomeIcon name='edit' />,
                    onClick: onClickEditName
                },
                {
                    label: t('products.menu.editVat', 'BTW aanpassen…'),
                    icon: <AwesomeIcon name='percent' />,
                    onClick: onClickEditVat
                }
            );
        options.push({
            label: t('products.menu.editCosts', 'Kosten aanpassen…'),
            icon: <AwesomeIcon name='euro-sign' />,
            onClick: onClickEditCosts
        });
        if (!canEdit) return options;
        if (product.hiddenByUser)
            options.push({
                label: t('products.menu.show', 'Tonen'),
                icon: <AwesomeIcon name='eye' />,
                onClick: onClickToggleHidden,
                disabled: updating
            });
        else
            options.push({
                label: t('products.menu.hide', 'Verbergen…'),
                icon: <AwesomeIcon name='eye-slash' />,
                onClick: onClickToggleHidden,
                disabled: updating,
                danger: true
            });
        return options;
    }

    // The three states share one filter bucket, so they must be told apart by
    // colour, icon and wording at once — not by colour alone.
    function renderStatusBadge() {
        const onClickBadge = canEdit && !updating ? onClickToggleHidden : undefined;
        if (status === 'visible')
            return (
                <BluemateBadge
                    label={renderStatusLabel('eye', t('products.status.visible', 'Zichtbaar'))}
                    color='green'
                    size='big'
                    outline
                    onClick={onClickBadge}
                />
            );
        if (status === 'inactive')
            return (
                <BluemateBadge
                    label={renderStatusLabel('triangle-exclamation', t('products.status.inactive', 'Inactief op bol'))}
                    color='gray'
                    size='big'
                    outline
                    tooltip={t(
                        'products.status.inactiveTooltip',
                        'Dit product is niet meer actief op bol. Het telt niet mee in e-mails en notificaties. Deze status komt van bol en verandert niet als je het product hier verbergt of toont.'
                    )}
                    tooltipClassName='product-status-tooltip'
                />
            );
        return (
            <BluemateBadge
                label={renderStatusLabel('eye-slash', t('products.status.hidden', 'Verborgen'))}
                color='orange'
                size='big'
                outline
                tooltip={t(
                    'products.status.hiddenTooltip',
                    'Je hebt dit product zelf verborgen. Het wordt niet meer getoond in lijsten en overzichten en niet gebruikt in e-mails en notificaties. Deze wijziging geldt alleen voor Bluemate, niet op bol zelf — je kunt het hier weer tonen.'
                )}
                tooltipClassName='product-status-tooltip'
                onClick={onClickBadge}
            />
        );
    }

    function renderStatusLabel(icon: IconName, label: string) {
        return (
            <span className='status-label'>
                <AwesomeIcon name={icon} />
                {label}
            </span>
        );
    }

    function renderVats() {
        return (
            <div className='vat-values' onClick={onClickVat}>
                {!vatCountryCodes.length ? (
                    <label className='cell-sub'>{t('products.vat.default', 'Standaard')}</label>
                ) : (
                    <div className='vat-chips'>
                        {vatCountryCodes.map((countryCode: string) => (
                            <label key={countryCode} className='vat-chip'>
                                <CountryFlag countryCode={countryCode} />
                                <span className='vat-value'>
                                    {translateVatValue(product.vats.getCountryValue(countryCode))}
                                </span>
                            </label>
                        ))}
                    </div>
                )}
                {canEdit && <AwesomeIcon name='pencil' className='edit-hint' onClick={onClickVat} />}
            </div>
        );
    }

    function getScansTooltip() {
        if (product.qrScanCounts === null)
            return t(
                'products.scansUnavailable',
                'Voor verborgen en op bol inactieve producten worden scans hier niet getoond. Scans van gedrukte QR-codes blijven wel geregistreerd.'
            );
        return t('products.scansTooltip', 'Het aantal keren dat deze QR-code is gescand');
    }

    function renderDownloadButton() {
        const button = (
            <div className='download-btn-wrap'>
                <BluemateButton
                    onClick={onClickDownloadCode}
                    label={downloading ? <SpinnerIcon /> : <AwesomeIcon name='qrcode' />}
                    disabled={!canDownload || !isVisible || downloading}
                    tertiary
                    shape='square'
                    size='small'
                    className='download-btn'
                />
            </div>
        );
        return <BluemateTooltip text={getDownloadTooltip()}>{button}</BluemateTooltip>;
    }

    function getDownloadTooltip() {
        if (!canDownload)
            return t(
                'products.download.unavailable',
                'Geen QR-code beschikbaar: dit product heeft nog geen productpagina op bol.'
            );
        // The backend only generates codes for active products, so the button
        // says why instead of failing after the click.
        if (!isVisible)
            return t(
                'products.download.onlyVisible',
                'QR-codes zijn alleen beschikbaar voor zichtbare producten. Toon dit product weer, of wacht tot het op bol weer actief is.'
            );
        return t(
            'products.download.tooltip',
            'Download de QR-code die linkt naar het schrijven van een review. Voeg hem toe aan je verpakking om meer productreviews op bol te krijgen.'
        );
    }

    return (
        <div className={classNames('products-list-row', `status-${status}`)} ref={rowRef}>
            <div className='row-image'>
                <BluemateImageFallback src={product.imageURL} imageClassName='product-image' />
            </div>
            <div className='row-cell product-cell'>
                <label className='cell-main product-title' onClick={onClickTitle}>
                    <span className='title-text'>{nullify(product.title) ?? '-'}</span>
                    {canEdit && <AwesomeIcon name='pencil' className='edit-hint' />}
                </label>
                <div className='with-copy-action'>
                    <label className='cell-sub'>EAN: {product.ean}</label>
                    <CopyIcon onClick={onCopy(product.ean, true)} className='copy-action' autoReset />
                </div>
            </div>
            <div className='row-cell vat-cell'>
                <label className='cell-label'>{t('products.vat.label', 'BTW')}</label>
                {renderVats()}
            </div>
            <div className='row-cell costs-cell'>
                <label className='cell-label'>{t('products.costs.label', 'Kosten')}</label>
                <div className='costs-values' onClick={onClickCosts}>
                    <label className='cell-sub costs-value'>
                        {costs === undefined
                            ? '…'
                            : costs === null
                              ? t('products.costs.notSet', 'Niet ingevuld')
                              : formatCurrency(costs)}
                    </label>
                    <AwesomeIcon name='pencil' className='edit-hint' />
                </div>
            </div>
            <div className='row-cell status-cell'>{renderStatusBadge()}</div>
            <div className='row-cell scans-cell'>
                <BluemateTooltip text={getScansTooltip()}>
                    <label className='cell-main'>{product.qrScanCounts ?? '-'}</label>
                </BluemateTooltip>
                <label className='cell-sub'>{t('products.scansLabel', 'QR-scans')}</label>
            </div>
            <div className='row-actions'>
                {renderDownloadButton()}
                <BluemateMenu options={buildMenuOptions} align='right' className='row-options' />
            </div>
        </div>
    );
}

export default React.memo(ProductRow);
